import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import OutsideClickHandler from 'react-outside-click-handler';
import { Puff } from 'react-loader-spinner';
import FocusTrap from 'focus-trap-react';
import { MdClose } from 'react-icons/md';

import getPercentageDiscountPrice from '@repo/utils/getPercentageDiscountPrice';
import canApplyProStaffDiscount from '@repo/utils/canApplyProStaffDiscount';
import { pushToDataLayer } from '@repo/utils';
import styles from '@repo/ui-components/styles';
import { CartContext } from '@repo/contexts/CartContext';
import { CustomerContext } from '@repo/contexts/CustomerContext';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import IconButton from '@repo/ui-components/IconButton';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import Button from '../button';
import LineItem from './LineItem';
import CheckoutArea from './CheckoutArea';
import ShippingPromo from './ShippingPromo';

// used to calculate both header height and bottom offset
const HeaderHeight = '3rem';

const Modal = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  z-index: 99999;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
`;

const CartWrapper = styled.div`
  min-width: 20rem;
  max-width: 25rem;
  width: 100%;
  height: 100vh;
  background: #f7f7f7;
  position: absolute;
  top: 0;
  right: 0;
  overflow: hidden;
  transition: all 0.25s ease-out;
  transform: translateX(${({ visible }) => (visible ? '0' : '90%')});
`;

const CartHeader = styled.div`
  color: ${({ textColor }) => textColor};
  display: flex;
  align-items: center;
  background: ${({ backgroundColor }) => backgroundColor};
  height: ${HeaderHeight};
  padding: ${styles.spacing['spacing-md']};
  font-weight: bolder;
  font-size: ${styles.fontSize.lg};
  position: relative;
  text-transform: ${({ theme }) => theme.button.textTransform};
`;

const CartInner = styled.div`
  overflow: auto;
  color: ${({ textColor }) => textColor};
  height: calc(100% - ${HeaderHeight});
  background: #dddddd;

  button {
    cursor: pointer;
  }
`;

const LineItemsContainer = styled.ul`
  margin: 0;
  padding: 0;
  background: #f7f7f7;
  list-style-type: none;
`;

const EmptyCartMessage = styled.div`
  padding: 1rem;
  text-align: center;

  h3 {
    font-weight: bold;
    font-size: 1.25rem;
    margin: 0;
    padding: 0;
  }
`;

const Spinner = styled.div`
  display: flex;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: fixed;
  z-index: 3;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
`;

const DealerInformation = styled.div`
  line-height: 1.15;

  padding: ${styles.spacing['spacing-md']};
  p {
    margin: 0;
    font-size: ${styles.fontSize.base};
  }
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  right: ${styles.spacing['spacing-1xs']};
`;

let invalidCartCheck = false;

function Cart({
  headerBackgroundColor = '#000000',
  headerTextColor = '#ffffff',
  textColor = '#777777',
  highlightColor = '#ff0099',
  setVisible = false,
}) {
  const {
    cart,
    cartActive,
    setCartActive,
    cartBusy,
    incrementLineItem,
    decrementLineItem,
    deleteLineItem,
    emptyCart,
  } = useContext(CartContext);

  const { tt } = useUiComponentsInternationalContext();

  const [modalVisible, setModalVisible] = useState(setVisible);
  const [sliderVisible, setSliderVisible] = useState(setVisible);

  const { lines, cost, checkoutUrl } = cart;
  const context = useContext(CustomerContext);

  const dealerContext = useContext(DealerSearchContext);

  const { storedDealer } = dealerContext || {};

  useEffect(() => {
    if (!invalidCartCheck && cart?.lines?.edges?.length) {
      cart.lines.edges.forEach(({ node }) => {
        if (!node.merchandise) emptyCart();
      });
      invalidCartCheck = true;
    }
  }, [cart.lines]);

  const getPrice = price => {
    if (context?.isProStaff()) {
      const array = [];

      cart.lines.edges.forEach(({ node }) => {
        const tags = node?.attributes?.[0]?.value.split(',');

        if (canApplyProStaffDiscount(tags, node?.merchandise?.compareAtPrice)) {
          const discountedPrice = getPercentageDiscountPrice({
            price: node?.merchandise?.price?.amount,
            percentageDiscount:
              process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
          });
          array.push(parseFloat(discountedPrice) * node.quantity);
        } else {
          array.push(parseFloat(node.merchandise.price.amount) * node.quantity);
        }
      });
      const sum = array.reduce((total, lineItemPrice) => total + lineItemPrice);
      const totalPrice = sum.toFixed(2).toString();
      return totalPrice;
    }
    return price;
  };

  useEffect(() => {
    if (cartActive) {
      setModalVisible(true);
      setSliderVisible(true);

      pushToDataLayer('view_cart', {
        ecommerce: {
          currency: cost?.subtotalAmount?.currencyCode,
          value: cost?.subtotalAmount?.amount,
        },
        items:
          lines?.edges?.map(edge => ({
            variant: edge.node?.merchandise,
            product: edge.node?.merchandise?.product,
            quantity: edge.node?.quantity,
          })) ?? [],
      });
    }
  }, [cartActive]);

  useEffect(() => {
    if (!sliderVisible) {
      setTimeout(() => {
        setModalVisible(false);
      }, 125); // allow time for transition to happen
    }
  }, [sliderVisible]);

  useEffect(() => {
    if (!modalVisible) {
      setTimeout(() => {
        setCartActive(false);
      }, 125); // allow time for transition to happen
    }
  }, [modalVisible]);

  return (
    cartActive && (
      <Modal visible={modalVisible}>
        <OutsideClickHandler
          onOutsideClick={() => {
            setSliderVisible(false);
          }}
        >
          <FocusTrap>
            <CartWrapper visible={sliderVisible}>
              {cartBusy && (
                <Spinner>
                  <Puff
                    color={highlightColor}
                    height={120}
                    width={120}
                    timeout={300000}
                  />
                </Spinner>
              )}

              <CartHeader
                backgroundColor={headerBackgroundColor}
                textColor={headerTextColor}
              >
                <div>
                  {tt(s => s.cartCommon.shoppingCartWithQuantity, {
                    totalQuantity: cart?.totalQuantity || 0,
                  })}
                </div>
                <CloseButtonContainer>
                  <IconButton
                    ariaLabelledBy={tt(
                      s => s.cartCommon.closeCartAriaLabelledBy
                    )}
                    title={tt(s => s.cartCommon.closeCartButtonTitle)}
                    onClick={() => {
                      setSliderVisible(false);
                    }}
                    icon={
                      <MdClose
                        size={28}
                        fill={headerTextColor}
                        alt={tt(s => s.cartCommon.closeAlt)}
                      />
                    }
                  />
                </CloseButtonContainer>
              </CartHeader>
              <CartInner textColor={textColor}>
                {lines.edges?.length >= 1 && (
                  <ShippingPromo
                    cartTotal={cost?.subtotalAmount?.amount}
                    hasSubscriptionItems={lines.edges?.some(
                      // check if any line item has a selling plan allocation
                      edge => edge.node.sellingPlanAllocation !== null
                    )}
                  />
                )}
                <LineItemsContainer>
                  {lines?.edges?.length !== 0 && storedDealer && (
                    <DealerInformation>
                      <p>{tt(s => s.cartCommon.fulfilledBy)}</p>
                      <p>
                        <strong>{storedDealer?.name}</strong>
                      </p>
                    </DealerInformation>
                  )}
                  {lines?.edges?.map(
                    ({ node: { id, merchandise, quantity, attributes } }) => (
                      <LineItem
                        key={merchandise.id}
                        id={id}
                        variant={merchandise}
                        quantity={quantity}
                        title={merchandise.product.title}
                        highlightColor={highlightColor}
                        textColor={textColor}
                        increaseQuantityHandler={incrementLineItem}
                        decreaseQuantityHandler={decrementLineItem}
                        deleteLineItemHandler={deleteLineItem}
                        customAttributes={attributes}
                      />
                    )
                  )}
                </LineItemsContainer>
                {lines?.edges?.length !== 0 && (
                  <CheckoutArea
                    subtotalPrice={getPrice(cost?.subtotalAmount?.amount)}
                    checkoutUrl={checkoutUrl}
                  />
                )}
                {lines?.edges?.length === 0 && (
                  <EmptyCartMessage>
                    <br />
                    <h3>{tt(s => s.cartCommon.noItemsInShippingCartHeader)}</h3>
                    <br />
                    <Button
                      onClick={() => {
                        setSliderVisible(false);
                      }}
                    >
                      {tt(s => s.cartCommon.continueShoppingToastButton)}
                    </Button>
                  </EmptyCartMessage>
                )}
              </CartInner>
            </CartWrapper>
          </FocusTrap>
        </OutsideClickHandler>
      </Modal>
    )
  );
}

Cart.propTypes = {
  headerBackgroundColor: PropTypes.string,
  headerTextColor: PropTypes.string,
  highlightColor: PropTypes.string,
  textColor: PropTypes.string,
  setVisible: PropTypes.bool,
};

export default Cart;
